import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ConnectionStatus, WebSocketService } from '@freelancer/datastore';
import { ScreenSizeService } from '@freelancer/screen-size';
import {
  ToastAlertColor,
  ToastAlertService,
  ToastAlertType,
} from '@freelancer/ui/toast-alert';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription, combineLatest } from 'rxjs';
import { filter, map, pairwise, startWith } from 'rxjs/operators';

@UntilDestroy({ className: 'NetworkAlertService' })
@Injectable({ providedIn: 'root' })
export class NetworkAlertService {
  private websocket = inject(WebSocketService);
  private toastAlertService = inject(ToastAlertService);
  private screenSize = inject(ScreenSizeService);
  private platformId = inject(PLATFORM_ID);

  private subscription = new Subscription();

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.subscription.add(
      combineLatest([
        this.websocket.getConnectStatusForUser(),
        this.screenSize.isMobile(),
      ])
        .pipe(
          filter(([, isMobile]) => !isMobile),
          map(([detail]) => detail),
          startWith(undefined),
          pairwise(),
          untilDestroyed(this),
        )
        .subscribe(([prev, curr]) => {
          if (!curr) {
            return;
          }

          if (curr.status === ConnectionStatus.CLOSED) {
            this.toastAlertService.open({
              id: 'network-alert-offline',
              type: ToastAlertType.ERROR,
              color: ToastAlertColor.DARK,
              message: $localize`Unable to connect to chat, retrying in ${curr.retryDelayInSeconds}s`,
              closeable: true,
              indefinite: true,
              reopenIfExists: true,
            });
          } else if (prev?.status === ConnectionStatus.CLOSED) {
            this.toastAlertService.close('network-alert-offline');

            this.toastAlertService.open({
              id: 'network-alert-online',
              type: ToastAlertType.SUCCESS,
              color: ToastAlertColor.LIGHT,
              message: $localize`You are back online`,
              closeable: true,
              reopenIfExists: true,
            });
          }
        }),
    );
  }
}
